'use client';

import { useRef } from 'react';
import { BannerCarousel, type BannerCarouselItem } from './banner-carousel';
import { cn } from '../utils/cn';

export type HomeTabsShellTab = {
  id: string;
  label: string;
  activeColor: string;
};

interface HomeTabsShellProps {
  tabs: HomeTabsShellTab[];
  activeTab: string;
  activeIndex: number;
  banners?: BannerCarouselItem[];
  onBannerClick: (linkType: string | null, linkValue: string | null) => void;
  onTabClick: (tabId: string) => void;
  trendsContent: React.ReactNode;
  feedContent: React.ReactNode;
  ideasContent: React.ReactNode;
  historyContent: React.ReactNode;
}

export function HomeTabsShell({
  tabs,
  activeTab,
  activeIndex,
  banners = [],
  onBannerClick,
  onTabClick,
  trendsContent,
  feedContent,
  ideasContent,
  historyContent,
}: HomeTabsShellProps) {
  const tabsRef = useRef<HTMLElement>(null);

  const handleTabClick = (tabId: string, index: number) => {
    onTabClick(tabId);

    const nav = tabsRef.current;
    const button = nav?.children[index] as HTMLElement | undefined;
    if (!nav || !button) return;

    const navRect = nav.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();

    if (buttonRect.right > navRect.right) {
      nav.scrollBy({ left: buttonRect.right - navRect.right + 16, behavior: 'smooth' });
    }
    if (buttonRect.left < navRect.left) {
      nav.scrollBy({ left: buttonRect.left - navRect.left - 16, behavior: 'smooth' });
    }
  };

  const contentByTabId: Record<string, React.ReactNode> = {
    trends: trendsContent,
    feed: feedContent,
    ideas: ideasContent,
    history: historyContent,
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mt-1">
        <BannerCarousel banners={banners} onBannerClick={onBannerClick} />
      </div>

      <nav
        ref={tabsRef}
        className="flex gap-5 overflow-x-auto whitespace-nowrap px-4 py-3 scrollbar-hide"
      >
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            onClick={() => handleTabClick(tab.id, index)}
            className={cn(
              'flex-shrink-0 cursor-pointer border-none bg-transparent p-0 font-benzin text-xl font-semibold transition-colors duration-200',
              activeTab === tab.id ? tab.activeColor : 'text-muted-foreground',
            )}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="relative overflow-hidden">
        <div
          className="flex items-start transition-transform duration-300 ease-in-out"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {tabs.map((tab, index) => (
            <div
              key={tab.id}
              className={cn(
                'w-full flex-shrink-0 pb-[100px]',
                tab.id === 'feed' || tab.id === 'ideas' || tab.id === 'history' ? 'px-0' : 'px-4',
              )}
              style={{
                visibility: Math.abs(index - activeIndex) <= 1 ? 'visible' : 'hidden',
                height: index === activeIndex ? 'auto' : 0,
                overflow: index === activeIndex ? 'visible' : 'hidden',
              }}
            >
              {contentByTabId[tab.id] ?? null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
